import React, { useState } from 'react';
import { ArrowRightCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface HandoffButtonProps {
  loanOfficerId?: string;
  prospectId?: string;
  progressId?: string;
  onSuccess?: () => void;
}

const HandoffButton: React.FC<HandoffButtonProps> = ({
  loanOfficerId,
  prospectId,
  progressId,
  onSuccess,
}) => {
  const { loanOfficer: currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentRole = currentUser?.user_role === 'va' ? 'va' : 'admin';
  const targetRole = currentRole === 'admin' ? 'va' : 'admin';
  const targetRoleDisplay = targetRole === 'admin' ? 'Admin Team' : 'VA Team';

  const handleSubmit = async () => {
    if (!currentUser) return;

    setSubmitting(true);
    setError(null);

    try {
      await api.createHandoff({
        from_user_id: currentUser.id,
        from_role: currentRole as 'admin' | 'va',
        to_role: targetRole as 'admin' | 'va',
        loan_officer_id: loanOfficerId,
        prospect_id: prospectId,
        progress_id: progressId,
        message: message.trim() || undefined,
      });

      setIsOpen(false);
      setMessage('');
      onSuccess?.();
    } catch (err: any) {
      console.error('Error creating handoff:', err);
      setError(err.message || 'Failed to create handoff');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <ArrowRightCircleIcon className="h-5 w-5" />
        Hand Off to {targetRoleDisplay}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setIsOpen(false)}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              {/* Header */}
              <div className="mb-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowRightCircleIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Hand Off to {targetRoleDisplay}
                  </h2>
                </div>
                <p className="text-sm text-slate-500">
                  Send a handoff request to the {targetRoleDisplay}. They will receive an email
                  notification and see this in their dashboard.
                </p>
              </div>

              {/* Message Input */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add any notes or instructions for the team..."
                  className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                />
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <ArrowRightCircleIcon className="h-4 w-4" />
                      Send Handoff
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HandoffButton;
